import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { ClientItem } from './-components/ClientItem'
import { WelcomeSection } from './-components/WelcomeSection'
import { KpiCards } from './-components/KpiCards'
import { ClientStatusChart } from './-components/ClientStatusChart'
import { ForecastActivityChart } from './-components/ForecastActivityChart'
import { RecentForecastsTable } from './-components/RecentForecastsTable'
import { LoadingSkeleton } from './-components/LoadingSkeleton'
import { ErrorState } from './-components/ErrorState'
import type { Forecast } from '@/types/forecast.types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Fade, Fades } from '@/components/animate-ui/primitives/effects/fade'
import { ForecastWizard } from '@/components/forecast/ForecastWizard'
import { useHomeData } from '@/hooks/use-cases/useHomeData'
import { useAuth } from '@/contexts/AuthContext'
import { PageTitle } from '@/components/PageTitle'

export const Route = createFileRoute('/_app/home/')({
  component: HomePage,
})

function HomePage() {
  const { user } = useAuth()
  const userName = user?.firstName || user?.username || 'there'

  const {
    allClients,
    recentClients,
    favoriteClients,
    draftForecasts,
    publishedForecasts,
    isLoading,
    isError,
    error,
    refetchAll,
  } = useHomeData()

  const [wizardOpen, setWizardOpen] = React.useState(false)
  const [selectedForecast, setSelectedForecast] = React.useState<
    Forecast | undefined
  >(undefined)

  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (isError && error) {
    return <ErrorState error={error} refetch={refetchAll} />
  }

  const allForecasts = [...draftForecasts, ...publishedForecasts]

  const userId = user?.databaseId
  const myClients = allClients.filter((c) => c.advisor.id === userId).length
  const specialistClients = allClients.filter(
    (c) => c.specialist?.id === userId,
  ).length
  const plannerClients = allClients.filter(
    (c) => c.planner?.id === userId,
  ).length
  const teamClients = allClients.filter((c) =>
    c.team.some((t) => t.memberId === userId),
  ).length

  const handleForecastClick = (forecast: Forecast) => {
    setSelectedForecast(forecast)
    setWizardOpen(true)
  }

  // Find the full Client object matching the selected forecast's client id
  const selectedClient = selectedForecast
    ? allClients.find((c) => c.id === selectedForecast.client.id)
    : undefined

  return (
    <div className="space-y-3 sm:space-y-4">
      <PageTitle title="Dashboard" />
      {/* Welcome Header */}
      <WelcomeSection userName={userName} />

      {/* KPI Stats Row */}
      <KpiCards
        myClients={myClients}
        specialistClients={specialistClients}
        plannerClients={plannerClients}
        teamClients={teamClients}
        draftForecasts={draftForecasts.length}
        publishedForecasts={publishedForecasts.length}
      />

      {/* Charts Row */}
      <div className="grid gap-3 sm:gap-4 lg:grid-cols-2">
        <Fade inView inViewOnce delay={0}>
          <ClientStatusChart clients={allClients} />
        </Fade>
        <Fade inView inViewOnce delay={100}>
          <ForecastActivityChart forecasts={allForecasts} />
        </Fade>
      </div>

      {/* Recent Forecasts Table */}
      <Fade inView inViewOnce delay={150}>
        <RecentForecastsTable
          forecasts={allForecasts}
          onForecastClick={handleForecastClick}
        />
      </Fade>

      {/* Favorite + Recent Clients */}
      <div className="grid gap-3 sm:gap-4 lg:grid-cols-2">
        <Fade inView inViewOnce delay={200}>
          <Card className="h-full">
            <CardHeader>
              <CardTitle>Favorite Clients</CardTitle>
            </CardHeader>
            <CardContent>
              {favoriteClients.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No favorite clients yet.
                </p>
              ) : (
                <div className="space-y-2">
                  <Fades
                    inView
                    inViewOnce
                    holdDelay={100}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  >
                    {favoriteClients.map((client) => (
                      <ClientItem key={client.id} client={client} />
                    ))}
                  </Fades>
                </div>
              )}
            </CardContent>
          </Card>
        </Fade>
        <Fade inView inViewOnce delay={250}>
          <Card className="h-full">
            <CardHeader>
              <CardTitle>Recent Clients</CardTitle>
            </CardHeader>
            <CardContent>
              {recentClients.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No recent clients yet.
                </p>
              ) : (
                <div className="space-y-2">
                  <Fades
                    inView
                    inViewOnce
                    holdDelay={100}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  >
                    {recentClients.map((client) => (
                      <ClientItem key={client.id} client={client} />
                    ))}
                  </Fades>
                </div>
              )}
            </CardContent>
          </Card>
        </Fade>
      </div>

      {selectedClient && (
        <ForecastWizard
          open={wizardOpen}
          onOpenChange={(open) => {
            setWizardOpen(open)
            if (!open) setSelectedForecast(undefined)
          }}
          client={selectedClient}
          existingForecast={selectedForecast}
        />
      )}
    </div>
  )
}
